import React, { useState, useEffect } from 'react';
import { ExternalLink, Globe } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { getAuthHeaders } from '../../utils/authUtils';

interface LinkPreviewData {
  url: string;
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
}

interface LinkPreviewProps {
  url: string;
  isMine?: boolean;
}

// Extract URLs from text
export const extractUrls = (text: string): string[] => {
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  return text.match(urlRegex) || [];
};

// Render text with clickable links
export const renderTextWithLinks = (text: string, isMine: boolean = false): React.ReactNode => {
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  const parts: string[] = text.split(urlRegex);
  const urls: string[] = text.match(urlRegex) || [];
  
  const result: React.ReactNode[] = [];
  
  parts.forEach((part: string, index: number) => {
    if (urls.includes(part)) {
      result.push(
        <a
          key={`link-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className={`underline hover:opacity-80 ${isMine ? 'text-blue-100' : 'text-blue-600 dark:text-blue-400'}`}
        >
          {part}
        </a>
      );
    } else if (part) {
      result.push(<span key={`text-${index}`}>{part}</span>);
    }
  });
  
  return result;
};

// Link Preview Card Component
const LinkPreview: React.FC<LinkPreviewProps> = ({ url, isMine = false }) => {
  const [preview, setPreview] = useState<LinkPreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        setLoading(true);
        setError(false);
        
        const response = await fetch(`${API_BASE_URL}/api/link-preview?url=${encodeURIComponent(url)}`, {
          headers: getAuthHeaders()
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch preview');
        }
        
        const data = await response.json();
        if (data.title || data.description || data.image) {
          setPreview(data);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error fetching link preview:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();
  }, [url]);

  if (loading) {
    return (
      <div className={`mt-2 p-3 rounded-lg border animate-pulse ${
        isMine 
          ? 'bg-blue-500/20 border-blue-400/30' 
          : 'bg-gray-50 dark:bg-dark-bg-tertiary border-gray-200 dark:border-dark-border'
      }`}>
        <div className="flex gap-3">
          <div className={`w-16 h-16 rounded ${isMine ? 'bg-blue-400/30' : 'bg-gray-200 dark:bg-gray-600'}`} />
          <div className="flex-1 space-y-2">
            <div className={`h-4 rounded w-3/4 ${isMine ? 'bg-blue-400/30' : 'bg-gray-200 dark:bg-gray-600'}`} />
            <div className={`h-3 rounded w-1/2 ${isMine ? 'bg-blue-400/30' : 'bg-gray-200 dark:bg-gray-600'}`} />
          </div>
        </div>
      </div>
    );
  }

  if (error || !preview) {
    return null;
  }

  const hostname = new URL(url).hostname.replace('www.', '');

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`mt-2 block rounded-lg border overflow-hidden hover:opacity-90 transition-opacity ${
        isMine 
          ? 'bg-blue-500/20 border-blue-400/30' 
          : 'bg-gray-50 dark:bg-dark-bg-tertiary border-gray-200 dark:border-dark-border'
      }`}
    >
      <div className="flex">
        {preview.image && (
          <div className="w-20 h-20 flex-shrink-0">
            <img
              src={preview.image}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}
        <div className="flex-1 p-2 min-w-0">
          <div className={`flex items-center gap-1 text-xs mb-1 ${
            isMine ? 'text-blue-200' : 'text-gray-500 dark:text-dark-text-muted'
          }`}>
            <Globe className="w-3 h-3" />
            <span className="truncate">{preview.siteName || hostname}</span>
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </div>
          {preview.title && (
            <p className={`text-sm font-medium line-clamp-1 ${
              isMine ? 'text-white' : 'text-gray-900 dark:text-dark-text-primary'
            }`}>
              {preview.title}
            </p>
          )}
          {preview.description && (
            <p className={`text-xs line-clamp-2 mt-0.5 ${
              isMine ? 'text-blue-100' : 'text-gray-600 dark:text-dark-text-secondary'
            }`}>
              {preview.description}
            </p>
          )}
        </div>
      </div>
    </a>
  );
};

export default LinkPreview;

